import { FC, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    OutlinedInput,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmatioDialog from 'ui-component/ConfirmationDialog';
import { Tenant } from 'types/tenant';
import { useDispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';

interface ChildComponentProps {
    tenant?: Tenant;
    onSave(tenant: Tenant): any;
    onDelete(tenant: Tenant): any;
    onClose(): any;
}

const TenantEdit: FC<ChildComponentProps> = (props) => {
    const { tenant, onSave, onDelete, onClose } = props;
    const [open, setOpen] = useState(false);
    const [currentTenant, setCurrentTenant] = useState<Tenant | undefined>(tenant);
    const theme = useTheme();
    const { t } = useTranslation();
    const [dialogDelete, setDialogDelete] = useState(false);
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();

    useEffect(() => {
        if (tenant === undefined) setOpen(false);
        else {
            setOpen(true);
            setCurrentTenant(tenant);
        }
    }, [tenant]);

    return (
        <>
            <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {t('EDIT')} tenant {tenant?.name}
                </DialogTitle>
                <DialogContent sx={{ paddingTop: '6px!important', width: mobileDevice ? window.innerWidth - 65 : 500 }}>
                    <Grid container>
                        <Grid item md={8} xs={12} sx={{ mt: 2 }}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('NAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={currentTenant?.name}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentTenant!,
                                            name: d.target.value
                                        };
                                        setCurrentTenant(e);
                                    }}
                                    label={t('NAME')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Typography marginTop="5px"> * {t('MANDATORY_FIELDS')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Grid
                        container
                        sx={{ alignItems: 'center', justifyContent: 'space-between', paddingLeft: '15px', paddingRight: '15px' }}
                    >
                        <Grid item>
                            <Button
                                style={{
                                    backgroundColor: theme.palette.error.main,
                                    borderRadius: 7,
                                    border: '2px solid',
                                    borderColor: theme.palette.error.main
                                }}
                                onClick={() => setDialogDelete(true)}
                            >
                                <Typography sx={{ color: 'white' }}> {t('DELETE')}</Typography>
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={onClose}
                                autoFocus
                                style={{
                                    border: '2px solid',
                                    borderColor: theme.palette.secondary.main,
                                    borderRadius: 7,
                                    marginRight: 10
                                }}
                            >
                                <Typography sx={{ color: theme.palette.primary.main }}> {t('CANCEL')}</Typography>
                            </Button>
                            <Button
                                onClick={() => onSave(currentTenant!)}
                                style={{
                                    border: '2px solid',
                                    borderColor: theme.palette.primary.main,
                                    borderRadius: 7,
                                    background: theme.palette.primary.main
                                }}
                            >
                                <Typography sx={{ color: '#fff' }}> {t('SAVE')}</Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>

            <ConfirmatioDialog
                isVisible={dialogDelete}
                confermationText="INSERT_TENANT_TO_CONFIRM_DELETE"
                onClose={() => {
                    setDialogDelete(false);
                }}
                onConfirm={(confermationValue?: string) => {
                    if (confermationValue?.trim().toLocaleLowerCase() === tenant?.name.trim().toLocaleLowerCase()) {
                        setDialogDelete(false);
                        onDelete(tenant!);
                    } else {
                        dispatch(startSnackbar(t('NAME_IS_WRONG'), 'error'));
                    }
                }}
            />
        </>
    );
};

export default TenantEdit;
